/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.5.0 - ScheduleHistoryTab v3.0
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file ScheduleHistoryTab.tsx
 * @description Tab de historial de envíos ejecutados - VERSIÓN COMPACTA
 * @version 3.0.0
 * @date 2025-11-28
 * 
 * FEATURES v3.0:
 * - ✅ Diseño compacto profesional (estilo ScheduleList v3.0)
 * - ✅ Stats inline con badges Bootstrap
 * - ✅ Búsqueda inteligente en tiempo real
 * - ✅ Filtros colapsables
 * - ✅ Tabla de 8 columnas optimizada
 * - ✅ Paginación custom (5/10/20/50)
 * - ✅ Export CSV/JSON
 * - ✅ Iconos de canales diferenciados por color
 * - ✅ Color WhatsApp #34E67E
 * - ✅ Badges Bootstrap nativos
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Badge,
  Button,
  ButtonGroup,
  Form,
  Collapse
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faDownload,
  faFilter,
  faTimes,
  faChartLine,
  faClock,
  faSearch,
  faChevronDown,
  faChevronUp,
  faPercentage
} from '@fortawesome/free-solid-svg-icons';
import {
  faWhatsapp,
  faInstagram,
  faFacebookF,
  faTelegram
} from '@fortawesome/free-brands-svg-icons';
import { ScheduleHistory } from '../../../types/scheduler.types';
import { useScheduleHistory } from '../../../hooks/useScheduleHistory';
import { formatDateSpanish, formatDuration } from '../../../types/dateUtils';

// ═══════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Export a CSV
 */
const exportToCSV = (history: ScheduleHistory[]) => {
  const headers = ['ID', 'Ejecutado', 'Canal', 'Campaña', 'Enviados', 'Fallidos', 'Tasa Éxito', 'Duración', 'Estado'];
  const rows = history.map(h => [
    h.id,
    formatDateSpanish(h.executedAt, true),
    h.channel,
    h.campaignName || 'Envío único',
    h.stats.sent,
    h.stats.failed,
    `${h.stats.successRate.toFixed(1)}%`,
    formatDuration(h.stats.duration),
    h.status
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `history_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Export a JSON
 */
const exportToJSON = (history: ScheduleHistory[]) => {
  const json = JSON.stringify(history, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `history_${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const ScheduleHistoryTab: React.FC = () => {
  // ═══════════════════════════════════════════════════════════════════════
  // HOOKS
  // ═══════════════════════════════════════════════════════════════════════
  
  const {
    history,
    stats,
    isLoading,
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters
  } = useScheduleHistory();

  // ═══════════════════════════════════════════════════════════════════════
  // LOCAL STATE
  // ═══════════════════════════════════════════════════════════════════════
  
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ═══════════════════════════════════════════════════════════════════════
  // STATUS CONFIG
  // ═══════════════════════════════════════════════════════════════════════
  
  const statusConfig = {
    success: { label: 'Exitoso', bg: 'success', icon: faCheckCircle },
    failed: { label: 'Fallido', bg: 'danger', icon: faTimesCircle },
    partial: { label: 'Parcial', bg: 'warning', icon: faExclamationTriangle }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // CHANNEL CONFIG
  // ═══════════════════════════════════════════════════════════════════════
  
  const channelConfig = {
    whatsapp: { icon: faWhatsapp, color: '#34E67E', label: 'WhatsApp' },
    instagram: { icon: faInstagram, color: '#E4405F', label: 'Instagram' },
    facebook: { icon: faFacebookF, color: '#1877F2', label: 'Facebook' },
    telegram: { icon: faTelegram, color: '#0088CC', label: 'Telegram' }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // COMPUTED STATS FROM HISTORY
  // ═══════════════════════════════════════════════════════════════════════
  
  const computedStats = useMemo(() => {
    if (!history || history.length === 0) {
      return {
        total: 0,
        successful: 0,
        failed: 0,
        partial: 0,
        averageSuccessRate: 0,
        byChannel: {} as Record<string, { total: number; successful: number; failed: number }>
      };
    }

    const successful = history.filter(h => h.status === 'success').length;
    const failed = history.filter(h => h.status === 'failed').length;
    const partial = history.filter(h => h.status === 'partial').length;
    
    const averageSuccessRate = history.reduce((sum, h) => sum + h.stats.successRate, 0) / history.length;
    
    // Calcular por canal
    const byChannel: Record<string, { total: number; successful: number; failed: number }> = {};
    history.forEach(h => {
      if (!byChannel[h.channel]) {
        byChannel[h.channel] = { total: 0, successful: 0, failed: 0 };
      }
      byChannel[h.channel].total++;
      if (h.status === 'success') byChannel[h.channel].successful++;
      if (h.status === 'failed') byChannel[h.channel].failed++;
    });

    return {
      total: history.length,
      successful,
      failed,
      partial,
      averageSuccessRate,
      byChannel
    };
  }, [history]);

  // ═══════════════════════════════════════════════════════════════════════
  // FILTERED & PAGINATED DATA
  // ═══════════════════════════════════════════════════════════════════════
  
  const filteredHistory = useMemo(() => {
    let result = history || [];
    
    // Filtro por status
    if (filters.status && filters.status.length > 0) {
      result = result.filter(h => filters.status!.includes(h.status));
    }
    
    // Filtro por canal
    if (filters.channel && filters.channel.length > 0) {
      result = result.filter(h => filters.channel!.includes(h.channel));
    }
    
    // Búsqueda por texto
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(h =>
        h.campaignName?.toLowerCase().includes(searchLower) ||
        h.id.toLowerCase().includes(searchLower) ||
        h.channel.toLowerCase().includes(searchLower)
      );
    }
    
    return result;
  }, [history, filters, searchTerm]);

  // Paginación
  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedHistory = filteredHistory.slice(startIndex, endIndex);

  // Reset página cuando cambia filtro o búsqueda
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  // ═══════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════
  
  const handleStatusFilter = (status: 'success' | 'failed' | 'partial') => {
    const currentStatuses = (filters.status || []) as ('success' | 'failed' | 'partial')[];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter(s => s !== status)
      : [...currentStatuses, status];
    
    setFilters({ ...filters, status: newStatuses });
  };

  const handleChannelFilter = (channel: keyof typeof channelConfig) => {
    const currentChannels = (filters.channel || []) as (keyof typeof channelConfig)[];
    const newChannels = currentChannels.includes(channel)
      ? currentChannels.filter(c => c !== channel)
      : [...currentChannels, channel];
    
    setFilters({ ...filters, channel: newChannels });
  };

  const handleClearFilters = () => {
    clearFilters();
    setSearchTerm('');
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER KPI CARDS
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderKPICards = () => {
    return (
      <Row className="mb-4">
        <Col xs={12} md={3}>
          <Card
            className="text-center"
            style={{
              borderRadius: '12px',
              border: '1px solid var(--bs-border-color)',
              backgroundColor: 'var(--bs-body-bg)',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Card.Body>
              <FontAwesomeIcon 
                icon={faHistory} 
                size="2x" 
                style={{ color: '#34E67E', marginBottom: '8px' }} 
              />
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--bs-body-color)' }}>
                {computedStats.total}
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)' }}>
                Total Ejecutados
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={3}>
          <Card
            className="text-center"
            style={{
              borderRadius: '12px',
              border: '1px solid var(--bs-border-color)',
              backgroundColor: 'var(--bs-body-bg)',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Card.Body>
              <FontAwesomeIcon 
                icon={faCheckCircle} 
                size="2x" 
                style={{ color: '#10b981', marginBottom: '8px' }} 
              />
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
                {computedStats.successful}
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)' }}>
                Exitosos
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={3}>
          <Card
            className="text-center"
            style={{
              borderRadius: '12px',
              border: '1px solid var(--bs-border-color)',
              backgroundColor: 'var(--bs-body-bg)',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Card.Body>
              <FontAwesomeIcon 
                icon={faTimesCircle} 
                size="2x" 
                style={{ color: '#ef4444', marginBottom: '8px' }} 
              />
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ef4444' }}>
                {computedStats.failed}
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)' }}>
                Fallidos
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={3}>
          <Card
            className="text-center"
            style={{
              borderRadius: '12px',
              border: '1px solid var(--bs-border-color)',
              backgroundColor: 'var(--bs-body-bg)',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Card.Body>
              <FontAwesomeIcon 
                icon={faChartLine} 
                size="2x" 
                style={{ color: '#34E67E', marginBottom: '8px' }} 
              />
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--bs-body-color)' }}>
                {computedStats.averageSuccessRate.toFixed(1)}%
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)' }}>
                Tasa de Éxito
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER HEADER COMPACTO
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderHeader = () => {
    return (
      <div 
        className="d-flex justify-content-between align-items-center mb-3 p-3"
        style={{
          backgroundColor: 'var(--bs-body-bg)',
          borderRadius: '12px',
          border: '1px solid var(--bs-border-color)'
        }}
      >
        <h6 className="mb-0" style={{ color: 'var(--bs-body-color)', fontWeight: 600, fontSize: '15px' }}>
          Historial de Envíos
        </h6>

        {/* Actions */}
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            style={{
              borderRadius: '8px',
              fontSize: '13px',
              padding: '6px 12px'
            }}
          >
            <FontAwesomeIcon icon={faFilter} className="me-1" />
            Filtros
            <FontAwesomeIcon 
              icon={showFilters ? faChevronUp : faChevronDown} 
              className="ms-1" 
              style={{ fontSize: '10px' }}
            />
          </Button>
          
          <ButtonGroup size="sm">
            <Button
              variant="outline-secondary"
              onClick={() => exportToCSV(filteredHistory)}
              title="Exportar CSV"
              style={{
                borderRadius: '8px 0 0 8px',
                fontSize: '13px',
                padding: '6px 12px'
              }}
            >
              <FontAwesomeIcon icon={faDownload} className="me-1" />
              CSV
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => exportToJSON(filteredHistory)}
              title="Exportar JSON"
              style={{
                borderRadius: '0 8px 8px 0',
                fontSize: '13px',
                padding: '6px 12px'
              }}
            >
              JSON
            </Button>
          </ButtonGroup>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER FILTROS COLAPSABLES
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderFilters = () => {
    return (
      <Collapse in={showFilters}>
        <div 
          className="mb-3 p-3"
          style={{
            backgroundColor: 'var(--bs-body-bg)',
            borderRadius: '12px',
            border: '1px solid var(--bs-border-color)'
          }}
        >
          <Row className="g-3">
            {/* Búsqueda */}
            <Col md={12}>
              <div className="input-group input-group-sm">
                <span className="input-group-text" style={{ backgroundColor: 'var(--bs-body-bg)' }}>
                  <FontAwesomeIcon icon={faSearch} style={{ color: 'var(--bs-secondary-color)' }} />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Buscar por campaña, ID o canal..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    fontSize: '13px',
                    border: '1px solid var(--bs-border-color)',
                    borderLeft: 'none'
                  }}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setSearchTerm('')}
                    style={{ fontSize: '13px' }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </div>
            </Col>

            {/* Status Filter */}
            <Col md={6}>
              <Form.Label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bs-secondary-color)', marginBottom: '6px' }}>
                ESTADO
              </Form.Label>
              <div className="d-flex gap-2 flex-wrap">
                {(Object.keys(statusConfig) as Array<keyof typeof statusConfig>).map(status => (
                  <Badge
                    key={status}
                    bg="none"
                    style={{
                      backgroundColor: (filters.status && filters.status.includes(status))
                        ? statusConfig[status].bg === 'success' ? '#10b98120' : 
                          statusConfig[status].bg === 'danger' ? '#ef444420' : '#f59e0b20'
                        : 'var(--bs-secondary-bg)',
                      color: (filters.status && filters.status.includes(status)) 
                        ? statusConfig[status].bg === 'success' ? '#10b981' :
                          statusConfig[status].bg === 'danger' ? '#ef4444' : '#f59e0b'
                        : 'var(--bs-body-color)',
                      border: `1px solid ${(filters.status && filters.status.includes(status)) 
                        ? (statusConfig[status].bg === 'success' ? '#10b981' :
                          statusConfig[status].bg === 'danger' ? '#ef4444' : '#f59e0b')
                        : 'var(--bs-border-color)'}`,
                      cursor: 'pointer',
                      padding: '4px 10px',
                      fontSize: '11px',
                      fontWeight: 500
                    }}
                    onClick={() => handleStatusFilter(status)}
                  >
                    <FontAwesomeIcon icon={statusConfig[status].icon} style={{ fontSize: '10px' }} className="me-1" />
                    {statusConfig[status].label}
                  </Badge>
                ))}
              </div>
            </Col>

            {/* Channel Filter */}
            <Col md={6}>
              <Form.Label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bs-secondary-color)', marginBottom: '6px' }}>
                CANAL
              </Form.Label>
              <div className="d-flex gap-2 flex-wrap">
                {(Object.keys(channelConfig) as Array<keyof typeof channelConfig>).map(channel => (
                  <Badge
                    key={channel}
                    bg="none"
                    style={{
                      backgroundColor: (filters.channel && filters.channel.includes(channel))
                        ? `${channelConfig[channel].color}20`
                        : 'var(--bs-secondary-bg)',
                      color: (filters.channel && filters.channel.includes(channel)) 
                        ? channelConfig[channel].color 
                        : 'var(--bs-body-color)',
                      border: `1px solid ${(filters.channel && filters.channel.includes(channel)) ? channelConfig[channel].color : 'var(--bs-border-color)'}`,
                      cursor: 'pointer',
                      padding: '4px 10px',
                      fontSize: '11px',
                      fontWeight: 500
                    }}
                    onClick={() => handleChannelFilter(channel)}
                  >
                    <FontAwesomeIcon icon={channelConfig[channel].icon} style={{ fontSize: '10px' }} className="me-1" />
                    {channelConfig[channel].label}
                  </Badge>
                ))}
              </div>
            </Col>
          </Row>

          {(hasActiveFilters || searchTerm) && (
            <div className="mt-2">
              <Button
                variant="link"
                size="sm"
                onClick={handleClearFilters}
                style={{ 
                  color: '#ef4444', 
                  padding: 0,
                  fontSize: '12px',
                  textDecoration: 'none'
                }}
              >
                <FontAwesomeIcon icon={faTimes} className="me-1" />
                Limpiar todos los filtros
              </Button>
            </div>
          )}
        </div>
      </Collapse>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER TABLE PROFESIONAL
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderTable = () => {
    if (isLoading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: '#34E67E' }} role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      );
    }

    if (filteredHistory.length === 0) {
      return (
        <div 
          className="text-center py-5"
          style={{
            backgroundColor: 'var(--bs-body-bg)',
            borderRadius: '12px',
            border: '1px solid var(--bs-border-color)'
          }}
        >
          <FontAwesomeIcon 
            icon={faHistory} 
            style={{ fontSize: '48px', color: 'var(--bs-secondary-color)', marginBottom: '16px' }} 
          />
          <p style={{ color: 'var(--bs-secondary-color)', fontSize: '14px', marginBottom: 0 }}>
            {(hasActiveFilters || searchTerm)
              ? 'No se encontraron registros que coincidan con los filtros'
              : 'No hay registros en el historial'}
          </p>
        </div>
      );
    }

    return (
      <div>
        {/* Contador y selector de items */}
        <div className="d-flex justify-content-between align-items-center mb-2 px-2">
          <small style={{ color: 'var(--bs-secondary-color)', fontSize: '12px' }}>
            Mostrando {startIndex + 1}-{Math.min(endIndex, filteredHistory.length)} de {filteredHistory.length} registros
          </small>
          
          <div className="d-flex align-items-center gap-2">
            <small style={{ color: 'var(--bs-secondary-color)', fontSize: '12px' }}>Por página:</small>
            <Form.Select
              size="sm"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              style={{ 
                width: 'auto',
                fontSize: '12px',
                padding: '4px 8px'
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </Form.Select>
          </div>
        </div>

        {/* Tabla */}
        <div 
          className="table-responsive"
          style={{
            backgroundColor: 'var(--bs-body-bg)',
            borderRadius: '12px',
            border: '1px solid var(--bs-border-color)',
            overflow: 'hidden'
          }}
        >
          <table className="table table-hover mb-0" style={{ fontSize: '13px' }}>
            <thead>
              <tr style={{ 
                backgroundColor: '#f9fafb',
                borderBottom: '2px solid #e5e7eb'
              }}>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  width: '140px'
                }}>
                  Ejecutado
                </th>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  width: '60px'
                }}>
                  Canal
                </th>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  minWidth: '150px'
                }}>
                  Campaña
                </th>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  width: '80px',
                  textAlign: 'center'
                }}>
                  Enviados
                </th>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  width: '80px',
                  textAlign: 'center'
                }}>
                  Fallidos
                </th>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  width: '100px',
                  textAlign: 'center'
                }}>
                  Tasa Éxito
                </th>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  width: '100px'
                }}>
                  Duración
                </th>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  width: '110px'
                }}>
                  Estado
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedHistory.map(historyItem => {
                const status = statusConfig[historyItem.status];
                const channel = channelConfig[historyItem.channel as keyof typeof channelConfig];

                return (
                  <tr
                    key={historyItem.id}
                    style={{
                      borderBottom: '1px solid #f3f4f6'
                    }}
                  >
                    {/* Executed At */}
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--bs-body-color)' }}>
                        {formatDateSpanish(historyItem.executedAt, true)}
                      </div>
                    </td>

                    {/* Channel */}
                    <td style={{ padding: '10px 12px' }}>
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: `${channel.color}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `1px solid ${channel.color}`
                        }}
                        title={channel.label}
                      >
                        <FontAwesomeIcon
                          icon={channel.icon}
                          style={{ color: channel.color, fontSize: '14px' }}
                        />
                      </div>
                    </td>

                    {/* Campaign */}
                    <td style={{ padding: '10px 12px' }}>
                      {historyItem.campaignName ? (
                        <span style={{ color: '#34E67E', fontWeight: 600, fontSize: '12px' }}>
                          {historyItem.campaignName}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--bs-secondary-color)', fontSize: '12px' }}>
                          Envío único
                        </span>
                      )}
                    </td>

                    {/* Sent */}
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <Badge bg="success" style={{ fontSize: '11px' }}>
                        {historyItem.stats.sent}
                      </Badge>
                    </td>

                    {/* Failed */}
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <Badge 
                        bg={historyItem.stats.failed > 0 ? 'danger' : 'secondary'}
                        style={{ fontSize: '11px' }}
                      >
                        {historyItem.stats.failed}
                      </Badge>
                    </td>

                    {/* Success Rate */}
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <Badge
                        bg={
                          historyItem.stats.successRate >= 90 ? 'success' :
                          historyItem.stats.successRate >= 70 ? 'warning' : 'danger'
                        }
                        text={historyItem.stats.successRate >= 70 ? 'dark' : undefined}
                        style={{ fontSize: '11px' }}
                      >
                        {historyItem.stats.successRate.toFixed(1)}%
                      </Badge>
                    </td>

                    {/* Duration */}
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontSize: '12px', color: 'var(--bs-secondary-color)' }}>
                        <FontAwesomeIcon icon={faClock} className="me-1" style={{ fontSize: '10px' }} />
                        {formatDuration(historyItem.stats.duration)}
                      </div>
                    </td>

                    {/* Status */}
                    <td style={{ padding: '10px 12px' }}>
                      <Badge bg={status.bg}>
                        <FontAwesomeIcon icon={status.icon} className="me-1" />
                        {status.label}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              style={{ fontSize: '12px', padding: '4px 12px' }}
            >
              Anterior
            </Button>

            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              // Mostrar solo páginas cercanas
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                    style={{
                      fontSize: '12px',
                      padding: '4px 12px',
                      minWidth: '32px'
                    }}
                  >
                    {page}
                  </Button>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return (
                  <span key={page} style={{ color: 'var(--bs-secondary-color)', fontSize: '12px' }}>
                    ...
                  </span>
                );
              }
              return null;
            })}

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{ fontSize: '12px', padding: '4px 12px' }}
            >
              Siguiente
            </Button>

            <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--bs-secondary-color)' }}>
              Página {currentPage} de {totalPages}
            </span>
          </div>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════
  
  return (
    <div>
      {/* KPI Cards */}
      {renderKPICards()}

      {/* Header Compacto */}
      {renderHeader()}

      {/* Filtros Colapsables */}
      {renderFilters()}

      {/* Tabla Profesional */}
      {renderTable()}
    </div>
  );
};

export default ScheduleHistoryTab;